import { useEffect, useRef } from 'react';

interface UseElementResizeOptions {
  /**
   * Укажите, на какие параметры реагировать:
   * - 'width'  => только при изменении ширины
   * - 'height' => только при изменении высоты
   * - 'both'   => если изменилась либо ширина, либо высота
   */
  measure?: 'width' | 'height' | 'both';

  /**
   * Колбэк, вызываемый при детектировании изменения.
   * В него передаётся (width, height, entry).
   */
  onResize?: (width: number, height: number, entry: ResizeObserverEntry) => void;
}

/**
 * Хук, который отслеживает размер элемента через ResizeObserver.
 * Вызывает onResize, когда действительно изменяются указанные (measure) размеры.
 */
export default function useElementResize(element?: HTMLElement | null, options: UseElementResizeOptions = {}) {
  const { measure = 'both', onResize } = options;

  const prevSizeRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!element || !onResize) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      if (entries.length === 0) {
        return;
      }

      const entry = entries[0];
      const { width, height } = entry.contentRect;

      const prev = prevSizeRef.current;
      let changed = false;

      if (!prev) {
        changed = true;
      } else {
        if (measure === 'width') {
          changed = width !== prev.width;
        } else if (measure === 'height') {
          changed = height !== prev.height;
        } else {
          changed = width !== prev.width || height !== prev.height;
        }
      }

      if (changed) {
        prevSizeRef.current = { width, height };
        onResize?.(width, height, entry);
      }
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [element, measure, onResize]);
}
